import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  Avatar,
  Switch,
  FormControlLabel,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Checkbox,
  InputAdornment,
} from '@mui/material';
import TogglePill from '../components/TogglePill';
import {
  UserIcon,
  ShieldCheckIcon,
  TrashIcon,
  PencilIcon,
  MagnifyingGlassIcon,
  UserGroupIcon,
  CheckBadgeIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  BuildingStorefrontIcon,
  EyeIcon,
  EyeSlashIcon,
} from '@heroicons/react/24/outline';
import { useTheme } from '../context/ThemeContext';
import AppIconButton from '../components/ui/AppIconButton';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../api/api';
import toast from 'react-hot-toast';
import CardLayoutTheme from '../theme/CardLayoutTheme';
import { CURRENCY } from '../config/currency';

interface User {
  id: number;
  username: string;
  email: string;
  name: string;
  roles: string[];
  isActive: boolean;
  emailVerified: boolean;
  vendorId: number | null;
  createdAt: string;
  updatedAt: string;
  stewardCode?: string;
  isOnDuty?: boolean;
}

interface Vendor {
  id: number;
  username: string;
  email: string;
  name: string;
  isActive: boolean;
  createdAt: string;
  userCount?: number;
  stewardCount?: number;
  // Extended details
  businessName?: string;
  phoneNumber?: string;
  address?: string;
  businessType?: string;
  defaultOrderType?: string;
  defaultPaymentMode?: string;
  serviceChargePercentage?: number;
  gstPercentage?: number;
  packagingChargePerOrder?: number;
}

interface UserStats {
  totalUsers: number;
  activeUsers: number;
  inactiveUsers: number;
  admins: number;
  vendors: number;
  customers: number;
  stewards: number;
  guests: number;
  verifiedEmails: number;
  unverifiedEmails: number;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`user-tabpanel-${index}`}
      aria-labelledby={`user-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{
          pt: 3,
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0
        }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const UserManagement: React.FC = () => {
  const { theme } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = Array.isArray(user?.roles) && user!.roles.includes('ROLE_ADMIN');
  const [tabValue, setTabValue] = useState(0);

  // Vendor states
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [expandedVendors, setExpandedVendors] = useState<Set<number>>(new Set());
  const [vendorUsers, setVendorUsers] = useState<Record<number, User[]>>({});
  const [vendorDetails, setVendorDetails] = useState<Record<number, Vendor>>({});
  const [loadingVendorUsers, setLoadingVendorUsers] = useState<Set<number>>(new Set());
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [vendorPage, setVendorPage] = useState(0);
  const [vendorRowsPerPage, setVendorRowsPerPage] = useState(10);
  const [vendorTotal, setVendorTotal] = useState(0);

  // Customer states
  const [customers, setCustomers] = useState<User[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [customerPage, setCustomerPage] = useState(0);
  const [customerRowsPerPage, setCustomerRowsPerPage] = useState(10);
  const [customerTotal, setCustomerTotal] = useState(0);
  const [customerFilter, setCustomerFilter] = useState<string>('');
  const [customerSearch, setCustomerSearch] = useState<string>('');

  // Vendor search state
  const [vendorSearch, setVendorSearch] = useState<string>('');
  const [vendorFilter, setVendorFilter] = useState<string>('');

  // Admin search state  
  const [adminSearch, setAdminSearch] = useState<string>('');
  const [adminFilter, setAdminFilter] = useState<string>('');

  // Admin states
  const [admins, setAdmins] = useState<User[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminPage, setAdminPage] = useState(0);
  const [adminRowsPerPage, setAdminRowsPerPage] = useState(10);
  const [adminTotal, setAdminTotal] = useState(0);

  // Common states
  const [stats, setStats] = useState<UserStats | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);

  // Create Customer/Admin dialog state
  const [addCustomerOpen, setAddCustomerOpen] = useState(false);
  const [addAdminOpen, setAddAdminOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [customerForm, setCustomerForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    isActive: true,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [adminForm, setAdminForm] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: '',
    isActive: true,
  });

  useEffect(() => {
    if (isAdmin) {
      fetchStats();
    }
    if (tabValue === 0) {
      fetchVendors();
    } else if (tabValue === 1) {
      fetchCustomers();
    } else if (tabValue === 2) {
      fetchAdmins();
    }
  }, [isAdmin, tabValue, vendorPage, vendorRowsPerPage, customerPage, customerRowsPerPage, adminPage, adminRowsPerPage]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/admin/users/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
      // Set default stats if API fails - don't block the page
      setStats({
        totalUsers: 0,
        activeUsers: 0,
        inactiveUsers: 0,
        admins: 0,
        vendors: 0,
        customers: 0,
        stewards: 0,
        guests: 0,
        verifiedEmails: 0,
        unverifiedEmails: 0,
      });
    }
  };

  const fetchVendors = async () => {
    setVendorsLoading(true);
    try {
      const params = new URLSearchParams({
        page: vendorPage.toString(),
        size: vendorRowsPerPage.toString(),
        role: 'VENDOR',
        sortBy: 'createdAt',
        sortDirection: 'DESC',
      });

      const response = await api.get(`/admin/users?${params.toString()}`);

      // Transform users with VENDOR role into vendor list
      const vendorList = response.data.users.map((user: User) => ({
        id: user.id,
        username: user.username,
        email: user.email,
        name: user.name || user.username,
        isActive: user.isActive,
        createdAt: user.createdAt,
        roles: user.roles || [],
      }));

      setVendors(vendorList);
      setVendorTotal(response.data.totalElements);
    } catch (error: any) {
      toast.error('Failed to fetch vendors');
      console.error('Error fetching vendors:', error);
    } finally {
      setVendorsLoading(false);
    }
  };

  const fetchVendorUsers = async (vendorId: number) => {
    setLoadingVendorUsers(prev => new Set(prev).add(vendorId));
    try {
      // Fetch all users associated with this vendor
      const params = new URLSearchParams({
        page: '0',
        size: '100', // Get all users for this vendor
        vendorId: vendorId.toString(),
        sortBy: 'createdAt',
        sortDirection: 'DESC',
      });

      const response = await api.get(`/admin/users?${params.toString()}`);
      setVendorUsers(prev => ({
        ...prev,
        [vendorId]: response.data.users,
      }));
    } catch (error: any) {
      toast.error('Failed to fetch vendor users');
      console.error('Error fetching vendor users:', error);
    } finally {
      setLoadingVendorUsers(prev => {
        const newSet = new Set(prev);
        newSet.delete(vendorId);
        return newSet;
      });
    }
  };

  const fetchCustomers = async () => {
    setCustomersLoading(true);
    try {
      const params = new URLSearchParams({
        page: customerPage.toString(),
        size: customerRowsPerPage.toString(),
        role: 'USER',
        sortBy: 'createdAt',
        sortDirection: 'DESC',
      });

      if (customerFilter === 'verified') {
        params.append('emailVerified', 'true');
      } else if (customerFilter === 'unverified') {
        params.append('emailVerified', 'false');
      } else if (customerFilter === 'active') {
        params.append('active', 'true');
      } else if (customerFilter === 'inactive') {
        params.append('active', 'false');
      }

      const response = await api.get(`/admin/users?${params.toString()}`);
      setCustomers(response.data.users);
      setCustomerTotal(response.data.totalElements);
    } catch (error: any) {
      toast.error('Failed to fetch customers');
      console.error('Error fetching customers:', error);
    } finally {
      setCustomersLoading(false);
    }
  };

  const fetchAdmins = async () => {
    setAdminsLoading(true);
    try {
      const params = new URLSearchParams({
        page: adminPage.toString(),
        size: adminRowsPerPage.toString(),
        role: 'ADMIN',
        sortBy: 'createdAt',
        sortDirection: 'DESC',
      });

      const response = await api.get(`/admin/users?${params.toString()}`);
      setAdmins(response.data.users);
      setAdminTotal(response.data.totalElements);
    } catch (error: any) {
      toast.error('Failed to fetch admins');
      console.error('Error fetching admins:', error);
    } finally {
      setAdminsLoading(false);
    }
  };

  const handleToggleVendor = async (vendorId: number) => {
    if (expandedVendors.has(vendorId)) {
      setExpandedVendors(prev => {
        const newSet = new Set(prev);
        newSet.delete(vendorId);
        return newSet;
      });
    } else {
      setExpandedVendors(prev => new Set(prev).add(vendorId));

      // Store basic vendor info (no detail API exists on backend)
      if (!vendorDetails[vendorId]) {
        const basicVendor = vendors.find(v => v.id === vendorId);
        if (basicVendor) {
          setVendorDetails(prev => ({
            ...prev,
            [vendorId]: basicVendor
          }));
        }
      }

      // Fetch vendor users if not already fetched
      if (!vendorUsers[vendorId]) {
        await fetchVendorUsers(vendorId);
      }
    }
  };

  const handleToggleUserStatus = async (user: User, isActive: boolean) => {
    try {
      const response = await api.put(`/admin/users/${user.id}/status`, {
        isActive: isActive,
      });

      toast.success(response.data.message);

      // Refresh the appropriate list
      if (tabValue === 0) {
        // Refresh vendor users if it's in vendor tab
        const vendorId = user.vendorId;
        if (vendorId && vendorUsers[vendorId]) {
          fetchVendorUsers(vendorId);
        }
      } else if (tabValue === 1) {
        fetchCustomers();
      } else if (tabValue === 2) {
        fetchAdmins();
      }

      fetchStats();
    } catch (error: any) {
      toast.error('Failed to update user status');
      console.error('Error updating user status:', error);
    }
  };

  const handleToggleEmailVerification = async (user: User) => {
    try {
      const response = await api.put(`/admin/users/${user.id}/verify-email`, {
        verified: !user.emailVerified,
      });

      toast.success(response.data.message);

      // Refresh the appropriate list
      if (tabValue === 1) {
        fetchCustomers();
      }

      fetchStats();
    } catch (error: any) {
      toast.error('Failed to update email verification');
      console.error('Error updating email verification:', error);
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      const response = await api.delete(`/admin/users/${userToDelete.id}`);
      toast.success(response.data.message);
      setDeleteDialogOpen(false);
      setUserToDelete(null);

      // Refresh the appropriate list
      if (tabValue === 0) {
        fetchVendors();
        const vendorId = userToDelete.vendorId;
        if (vendorId && vendorUsers[vendorId]) {
          fetchVendorUsers(vendorId);
        }
      } else if (tabValue === 1) {
        fetchCustomers();
      } else if (tabValue === 2) {
        fetchAdmins();
      }

      fetchStats();
    } catch (error: any) {
      toast.error('Failed to delete user');
      console.error('Error deleting user:', error);
    }
  };

  const getRoleColor = (role: string): string => {
    switch (role) {
      case 'ROLE_ADMIN':
        return theme.danger || '#DC2626';
      case 'ROLE_VENDOR':
        return theme.accent || '#7C3AED';
      case 'ROLE_STEWARD':
        return theme.components?.status?.pending?.text || '#2563EB';
      case 'ROLE_USER':
        return theme.success || '#10B981';
      case 'ROLE_GUEST':
        return theme.gray[500] || '#6B7280';
      default:
        return theme.gray[500] || '#6B7280';
    }
  };

  const getRoleLabel = (role: string): string => {
    return role.replace('ROLE_', '').toLowerCase();
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const renderUserRow = (user: User, isNested = false) => (
    <TableRow
      key={user.id}
      sx={isNested ? { backgroundColor: theme?.gray?.[50] || '#f9fafb' } : {}}
    >
      <TableCell sx={{ minWidth: '140px' }}>
        <Box display="flex" alignItems="center" gap={{ xs: 1, sm: 2 }}>
          {isNested && <Box sx={{ width: { xs: 15, sm: 20 } }} />}
          <Avatar sx={{
            width: { xs: 28, sm: 32 },
            height: { xs: 28, sm: 32 },
            fontSize: { xs: 12, sm: 14 }
          }}>
            {user.username[0].toUpperCase()}
          </Avatar>
          <Box sx={{ minWidth: 0, overflow: 'hidden' }}>
            <Typography
              variant="body2"
              fontWeight={500}
              sx={{
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {user.username}
            </Typography>
            {user.name && (
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}
              >
                {user.name}
              </Typography>
            )}
          </Box>
        </Box>
      </TableCell>
      <TableCell sx={{ minWidth: '200px' }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {user.email}
        </Typography>
      </TableCell>
      <TableCell sx={{ minWidth: '100px' }}>
        <Box display="flex" gap={0.5} flexWrap="wrap">
          {user.roles.map((role) => (
            <Chip
              key={role}
              label={getRoleLabel(role)}
              size="small"
              sx={{
                backgroundColor: `${getRoleColor(role)}20`,
                color: getRoleColor(role),
                border: `1px solid ${getRoleColor(role)}50`,
                fontWeight: 600,
                height: 24,
                borderRadius: '12px'
              }}
            />
          ))}
        </Box>
      </TableCell>
      <TableCell align="center" sx={{ minWidth: '80px' }}>
        <TogglePill checked={!!user.isActive} onChange={(v) => handleToggleUserStatus(user, v)} />
      </TableCell>
      {tabValue === 1 && (
        <TableCell align="center">
          <AppIconButton
            icon={user.emailVerified ? (
              <CheckBadgeIcon className="h-5 w-5" />
            ) : (
              <ExclamationTriangleIcon className="h-5 w-5" />
            )}
            tooltip={user.emailVerified ? 'Mark as Unverified' : 'Mark as Verified'}
            variant="ghost"
            size="small"
            onClick={() => handleToggleEmailVerification(user)}
            sx={{
              backgroundColor: `${user.emailVerified ? (theme.success || '#10B981') : (theme.warning || '#F59E0B')}20`,
              color: `${user.emailVerified ? (theme.success || '#10B981') : (theme.warning || '#F59E0B')}`,
              border: `1px solid ${user.emailVerified ? (theme.success || '#10B981') : (theme.warning || '#F59E0B')}50`,
              width: 36,
              height: 36,
              '&:hover': {
                backgroundColor: `${user.emailVerified ? (theme.success || '#10B981') : (theme.warning || '#F59E0B')}30`,
                borderColor: `${user.emailVerified ? (theme.success || '#10B981') : (theme.warning || '#F59E0B')}66`,
              }
            }}
          />
        </TableCell>
      )}
      <TableCell sx={{ minWidth: '120px' }}>
        <Typography
          variant="caption"
          sx={{
            fontSize: { xs: '0.7rem', sm: '0.75rem' },
            whiteSpace: 'nowrap'
          }}
        >
          {formatDate(user.createdAt)}
        </Typography>
      </TableCell>
      <TableCell align="center" sx={{ minWidth: '80px' }}>
        <AppIconButton
          variant="danger"
          tooltip="Delete User"
          icon={<TrashIcon className="h-3 w-3 sm:h-4 sm:w-4" />}
          size="small"
          onClick={() => {
            setUserToDelete(user);
            setDeleteDialogOpen(true);
          }}
        />
      </TableCell>
    </TableRow>
  );

  const renderVendorsTab = () => (
    <>
      {/* Kitchen Management Style Action Bar */}
      <Box sx={{
        p: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        backgroundColor: theme.components?.card?.background || '#ffffff'
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          {/* Search Input - Kitchen Management Style */}
          <TextField
            placeholder="Search vendors..."
            size="small"
            value={vendorSearch}
            onChange={(e) => setVendorSearch(e.target.value)}
            sx={{
              minWidth: { xs: 250, sm: 300 },
              width: { xs: '100%', sm: 'auto' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: theme.components?.card?.background || '#ffffff',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& fieldset': {
                  borderColor: theme.components?.input?.border || '#d1d5db',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover fieldset': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                  borderWidth: '2px',
                  boxShadow: theme.components?.input?.focusRing || '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }
              }
            }}
            InputProps={{
              startAdornment: <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 mr-2" />
            }}
          />

          {/* Enhanced Filter Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: '10px',
                backgroundColor: theme.components?.card?.background || '#ffffff',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.border || '#d1d5db',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                  borderWidth: '2px',
                  boxShadow: theme.components?.input?.focusRing || '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              <MenuItem value="" sx={{ '&:hover': { backgroundColor: 'var(--bg-sunken)' } }}>All Types</MenuItem>
              <MenuItem value="active" sx={{ '&:hover': { backgroundColor: 'var(--status-ok-bg)' } }}>Active Only</MenuItem>
              <MenuItem value="inactive" sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)' } }}>Inactive Only</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            onClick={fetchVendors}
            variant="outlined"
            size="small"
            disabled={vendorsLoading}
            sx={{
              minWidth: '40px',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              color: 'var(--text-link) !important',
              borderWidth: '1px !important',
              borderStyle: 'solid !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                borderColor: 'rgba(96, 165, 250, 0.4) !important',
              },
              '&:disabled': {
                opacity: 0.6
              }
            }}
          >
            <ArrowPathIcon className="h-4 w-4" />
          </Button>

          <Button
            startIcon={<BuildingStorefrontIcon className="h-4 w-4" />}
            variant="contained"
            size="small"
            onClick={() => navigate('/admin/vendor-onboarding')}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1.5,
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              color: 'var(--text-link) !important',
              borderWidth: '1px !important',
              borderStyle: 'solid !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                borderColor: 'rgba(96, 165, 250, 0.4) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Add Vendor
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{
        boxShadow: 'none',
        width: '100%',
        flex: 1,
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: { xs: '900px', sm: '1000px' }
        }
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '140px',
                whiteSpace: 'nowrap'
              }}>Vendor</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '200px',
                whiteSpace: 'nowrap'
              }}>Email</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '100px',
                whiteSpace: 'nowrap'
              }}>Status</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '100px',
                whiteSpace: 'nowrap'
              }}>Roles</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '120px',
                whiteSpace: 'nowrap'
              }}>Created</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '120px',
                whiteSpace: 'nowrap'
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {vendorsLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : vendors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>No vendors found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              vendors.map((vendor) => (
                <React.Fragment key={vendor.id}>
                  <TableRow>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={1}>
                        <AppIconButton
                          size="small"
                          icon={expandedVendors.has(vendor.id) ? (
                            <ChevronDownIcon className="h-7 w-7" style={{ strokeWidth: 2.5 }} />
                          ) : (
                            <ChevronRightIcon className="h-7 w-7" style={{ strokeWidth: 2.5 }} />
                          )}
                          tooltip="Toggle vendor details"
                          variant="primary"
                          onClick={() => handleToggleVendor(vendor.id)}
                        />
                        <Avatar sx={{ width: 32, height: 32, fontSize: 14 }}>
                          {vendor.username[0].toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {vendor.username}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {vendor.name}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>{vendor.email}</TableCell>
                    <TableCell align="center">
                      <Chip
                        label={vendor.isActive ? 'Active' : 'Inactive'}
                        size="small"
                        sx={{
                          backgroundColor: vendor.isActive ? '#d1fae5 !important' : '#fee2e2 !important',
                          color: vendor.isActive ? '#065f46 !important' : '#991b1b !important',
                          fontWeight: 600,
                          fontSize: '0.75rem',
                          borderRadius: '12px',
                          borderWidth: '1px',
                          borderStyle: 'solid',
                          borderColor: vendor.isActive ? '#86efac' : '#fca5a5',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={0.5} flexWrap="wrap">
                        {vendor.roles && vendor.roles.length > 0 ? vendor.roles.map((role) => (
                          <Chip
                            key={role}
                            label={getRoleLabel(role)}
                            size="small"
                            sx={{
                              backgroundColor: getRoleColor(role),
                              color: 'var(--text-inverse)',
                              fontWeight: 600,
                              fontSize: '0.7rem',
                              height: '24px',
                              borderRadius: '8px',
                              '& .MuiChip-label': {
                                px: 1.5
                              }
                            }}
                          />
                        )) : (
                          <Typography variant="caption" color="text.secondary">No roles</Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{formatDate(vendor.createdAt)}</TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={1} justifyContent="center" flexWrap="wrap">
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleToggleVendor(vendor.id)}
                          disabled={loadingVendorUsers.has(vendor.id)}
                          sx={{
                            backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
                            color: 'var(--text-link) !important',
                            borderWidth: '1px !important',
                            borderStyle: 'solid !important',
                            borderColor: 'rgba(96, 165, 250, 0.31) !important',
                            boxShadow: 'none !important',
                            textTransform: 'none !important',
                            fontWeight: '600 !important',
                            borderRadius: '8px',
                            px: 2,
                            py: 0.75,
                            minWidth: '100px',
                            '&:hover': {
                              backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                              borderColor: 'rgba(96, 165, 250, 0.4) !important',
                              boxShadow: 'none !important',
                            },
                            '&:disabled': {
                              opacity: 0.6
                            }
                          }}
                        >
                          {loadingVendorUsers.has(vendor.id) ? (
                            <CircularProgress size={16} sx={{ color: 'var(--status-info-text)' }} />
                          ) : expandedVendors.has(vendor.id) ? (
                            'Hide Users'
                          ) : (
                            'View Users'
                          )}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => navigate(`/admin/vendors/${vendor.id}/page-access`)}
                          sx={{
                            borderColor: 'rgba(147, 51, 234, 0.4) !important',
                            color: 'var(--accent-alt) !important',
                            borderWidth: '1px !important',
                            borderStyle: 'solid !important',
                            backgroundColor: 'rgba(147, 51, 234, 0.05) !important',
                            boxShadow: 'none !important',
                            textTransform: 'none !important',
                            fontWeight: '600 !important',
                            borderRadius: '8px',
                            px: 2,
                            py: 0.75,
                            minWidth: '100px',
                            '&:hover': {
                              backgroundColor: 'rgba(147, 51, 234, 0.1) !important',
                              borderColor: 'rgba(147, 51, 234, 0.5) !important',
                              boxShadow: 'none !important',
                            }
                          }}
                        >
                          Page Access
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                  {expandedVendors.has(vendor.id) && (
                    <>
                      <TableRow>
                        <TableCell colSpan={6} sx={{ py: 0, backgroundColor: 'var(--bg-sunken)' }}>
                          <Collapse in={expandedVendors.has(vendor.id)} timeout="auto" unmountOnExit>
                            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                              {/* Vendor Business Details Section */}
                              <Box sx={{ mb: 3 }}>
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 700,
                                    mb: 2,
                                    color: 'var(--text-primary)',
                                    fontSize: { xs: '0.95rem', sm: '1rem' },
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1
                                  }}
                                >
                                  <BuildingStorefrontIcon className="h-5 w-5" style={{ color: 'var(--text-link)' }} />
                                  Business Details
                                </Typography>

                                {vendorDetails[vendor.id] &&
                                  (vendorDetails[vendor.id].businessName ||
                                    vendorDetails[vendor.id].phoneNumber ||
                                    vendorDetails[vendor.id].address ||
                                    vendorDetails[vendor.id].businessType) ? (
                                  <Grid container spacing={2}>
                                    {/* Business Name */}
                                    {vendorDetails[vendor.id].businessName && (
                                      <Grid item xs={12} sm={6} md={4}>
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            backgroundColor: theme.components?.card?.background || '#ffffff',
                                            border: '1px solid var(--border-subtle)',
                                            borderRadius: '8px'
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.7rem' }}>
                                            BUSINESS NAME
                                          </Typography>
                                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: 'var(--text-primary)', fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
                                            {vendorDetails[vendor.id].businessName}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    )}

                                    {/* Phone Number */}
                                    {vendorDetails[vendor.id].phoneNumber && (
                                      <Grid item xs={12} sm={6} md={4}>
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            backgroundColor: theme.components?.card?.background || '#ffffff',
                                            border: '1px solid var(--border-subtle)',
                                            borderRadius: '8px'
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.7rem' }}>
                                            PHONE NUMBER
                                          </Typography>
                                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: 'var(--text-primary)', fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
                                            {vendorDetails[vendor.id].phoneNumber}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    )}

                                    {/* Business Type */}
                                    {vendorDetails[vendor.id].businessType && (
                                      <Grid item xs={12} sm={6} md={4}>
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            backgroundColor: theme.components?.card?.background || '#ffffff',
                                            border: '1px solid var(--border-subtle)',
                                            borderRadius: '8px'
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.7rem' }}>
                                            BUSINESS TYPE
                                          </Typography>
                                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 600, color: 'var(--text-primary)', fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
                                            {vendorDetails[vendor.id].businessType.replace(/_/g, ' ')}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    )}

                                    {/* Address */}
                                    {vendorDetails[vendor.id].address && (
                                      <Grid item xs={12}>
                                        <Paper
                                          elevation={0}
                                          sx={{
                                            p: 2,
                                            backgroundColor: theme.components?.card?.background || '#ffffff',
                                            border: '1px solid var(--border-subtle)',
                                            borderRadius: '8px'
                                          }}
                                        >
                                          <Typography variant="caption" sx={{ color: 'var(--text-tertiary)', fontWeight: 600, fontSize: '0.7rem' }}>
                                            ADDRESS
                                          </Typography>
                                          <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 500, color: 'var(--text-primary)', fontSize: { xs: '0.875rem', sm: '0.95rem' } }}>
                                            {vendorDetails[vendor.id].address}
                                          </Typography>
                                        </Paper>
                                      </Grid>
                                    )}

                                    {/* Financial Info - Compact Cards */}
                                    {(vendorDetails[vendor.id].serviceChargePercentage !== undefined ||
                                      vendorDetails[vendor.id].gstPercentage !== undefined ||
                                      vendorDetails[vendor.id].packagingChargePerOrder !== undefined) && (
                                        <>
                                          {vendorDetails[vendor.id].serviceChargePercentage !== undefined && (
                                            <Grid item xs={6} sm={4} md={3}>
                                              <Paper
                                                elevation={0}
                                                sx={{
                                                  p: 1.5,
                                                  backgroundColor: '#f0f9ff', /* basil-todo */
                                                  border: '1px solid var(--status-info-border)',
                                                  borderRadius: '8px'
                                                }}
                                              >
                                                <Typography variant="caption" sx={{ color: 'var(--status-info-text)', fontWeight: 600, fontSize: '0.65rem' }}>
                                                  SERVICE CHARGE
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, color: '#1e3a8a', fontSize: { xs: '0.875rem', sm: '1rem' } }}> {/* basil-todo */}
                                                  {vendorDetails[vendor.id].serviceChargePercentage}%
                                                </Typography>
                                              </Paper>
                                            </Grid>
                                          )}

                                          {vendorDetails[vendor.id].gstPercentage !== undefined && (
                                            <Grid item xs={6} sm={4} md={3}>
                                              <Paper
                                                elevation={0}
                                                sx={{
                                                  p: 1.5,
                                                  backgroundColor: 'var(--status-ok-bg)',
                                                  border: '1px solid #bbf7d0', /* basil-todo */
                                                  borderRadius: '8px'
                                                }}
                                              >
                                                <Typography variant="caption" sx={{ color: 'var(--status-ok-text)', fontWeight: 600, fontSize: '0.65rem' }}>
                                                  GST
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, color: 'var(--status-ok-text)', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                                  {vendorDetails[vendor.id].gstPercentage}%
                                                </Typography>
                                              </Paper>
                                            </Grid>
                                          )}

                                          {vendorDetails[vendor.id].packagingChargePerOrder !== undefined && (
                                            <Grid item xs={6} sm={4} md={3}>
                                              <Paper
                                                elevation={0}
                                                sx={{
                                                  p: 1.5,
                                                  backgroundColor: 'var(--status-warn-bg)',
                                                  border: '1px solid #fde047', /* basil-todo */
                                                  borderRadius: '8px'
                                                }}
                                              >
                                                <Typography variant="caption" sx={{ color: 'var(--status-warn-text)', fontWeight: 600, fontSize: '0.65rem' }}>
                                                  PACKAGING
                                                </Typography>
                                                <Typography variant="body2" sx={{ mt: 0.5, fontWeight: 700, color: 'var(--status-warn-text)', fontSize: { xs: '0.875rem', sm: '1rem' } }}>
                                                  {CURRENCY.symbol}{vendorDetails[vendor.id].packagingChargePerOrder}
                                                </Typography>
                                              </Paper>
                                            </Grid>
                                          )}
                                        </>
                                      )}
                                  </Grid>
                                ) : (
                                  <Alert severity="info" sx={{ mt: 1 }}>
                                    No detailed business information available for this vendor. Business details are set during vendor onboarding.
                                  </Alert>
                                )}

                                <Divider sx={{ my: 2 }} />
                              </Box>

                              {/* Users Section */}
                              {vendorUsers[vendor.id] && (
                                <Box>
                                  <Typography
                                    variant="subtitle1"
                                    sx={{
                                      fontWeight: 700,
                                      mb: 2,
                                      color: 'var(--text-primary)',
                                      fontSize: { xs: '0.95rem', sm: '1rem' },
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1
                                    }}
                                  >
                                    <UserGroupIcon className="h-5 w-5" style={{ color: 'var(--status-ok)' }} />
                                    Users associated with {vendor.name}
                                  </Typography>

                                  <Table size="small" sx={{ backgroundColor: theme.components?.card?.background || '#ffffff', borderRadius: '8px', overflow: 'hidden' }}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, backgroundColor: 'var(--bg-sunken)' }}>User</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, backgroundColor: 'var(--bg-sunken)' }}>Email</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, backgroundColor: 'var(--bg-sunken)' }}>Roles</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, backgroundColor: 'var(--bg-sunken)' }}>Status</TableCell>
                                        <TableCell sx={{ fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, backgroundColor: 'var(--bg-sunken)' }}>Created</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700, fontSize: { xs: '0.75rem', sm: '0.875rem' }, backgroundColor: 'var(--bg-sunken)' }}>Actions</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {vendorUsers[vendor.id].map((user) =>
                                        renderUserRow(user, true)
                                      )}
                                    </TableBody>
                                  </Table>
                                </Box>
                              )}
                            </Box>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </>
                  )}
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={vendorTotal}
        rowsPerPage={vendorRowsPerPage}
        page={vendorPage}
        onPageChange={(e, newPage) => setVendorPage(newPage)}
        onRowsPerPageChange={(e) => {
          setVendorRowsPerPage(parseInt(e.target.value, 10));
          setVendorPage(0);
        }}
        sx={{
          borderTop: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-sunken)'
        }}
      />
    </>
  );

  const renderCustomersTab = () => (
    <>
      {/* Kitchen Management Style Action Bar */}
      <Box sx={{
        p: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        backgroundColor: theme.components?.card?.background || '#ffffff'
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          {/* Search Input - Kitchen Management Style */}
          <TextField
            placeholder="Search customers..."
            size="small"
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            sx={{
              minWidth: { xs: 250, sm: 300 },
              width: { xs: '100%', sm: 'auto' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: theme.components?.card?.background || '#ffffff',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& fieldset': {
                  borderColor: theme.components?.input?.border || '#d1d5db',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover fieldset': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                  borderWidth: '2px',
                  boxShadow: theme.components?.input?.focusRing || '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }
              }
            }}
            InputProps={{
              startAdornment: <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 mr-2" />
            }}
          />

          {/* Enhanced Filter Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={customerFilter}
              onChange={(e) => {
                setCustomerFilter(e.target.value);
                setCustomerPage(0);
              }}
              displayEmpty
              sx={{
                borderRadius: '10px',
                backgroundColor: theme.components?.card?.background || '#ffffff',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.border || '#d1d5db',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                  borderWidth: '2px',
                  boxShadow: theme.components?.input?.focusRing || '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              <MenuItem value="" sx={{ '&:hover': { backgroundColor: 'var(--bg-sunken)' } }}>All Types</MenuItem>
              <MenuItem value="active" sx={{ '&:hover': { backgroundColor: 'var(--status-ok-bg)' } }}>Active Only</MenuItem>
              <MenuItem value="inactive" sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)' } }}>Inactive Only</MenuItem>
              <MenuItem value="verified" sx={{ '&:hover': { backgroundColor: 'var(--status-ok-bg)' } }}>Email Verified</MenuItem>
              <MenuItem value="unverified" sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)' } }}>Email Unverified</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            onClick={fetchCustomers}
            variant="outlined"
            size="small"
            disabled={customersLoading}
            sx={{
              minWidth: '40px',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              color: 'var(--text-link) !important',
              borderWidth: '1px !important',
              borderStyle: 'solid !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                borderColor: 'rgba(96, 165, 250, 0.4) !important',
              },
              '&:disabled': {
                opacity: 0.6
              }
            }}
          >
            <ArrowPathIcon className="h-4 w-4" />
          </Button>

          <Button
            startIcon={<UserIcon className="h-4 w-4" />}
            variant="contained"
            size="small"
            onClick={() => { setFormError(null); setCustomerForm({ username: '', email: '', password: '', confirmPassword: '', isActive: true }); setAddCustomerOpen(true); }}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1.5,
              backgroundColor: 'rgba(16, 185, 129, 0.2) !important',
              color: 'var(--status-ok) !important',
              borderWidth: '1px !important',
              borderStyle: 'solid !important',
              borderColor: 'rgba(16, 185, 129, 0.31) !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(16, 185, 129, 0.25) !important',
                borderColor: 'rgba(16, 185, 129, 0.4) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Add Customer
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{
        boxShadow: 'none',
        width: '100%',
        flex: 1,
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: { xs: '1100px', sm: '1200px' }
        }
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '140px',
                whiteSpace: 'nowrap'
              }}>Customer</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '200px',
                whiteSpace: 'nowrap'
              }}>Email</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '100px',
                whiteSpace: 'nowrap'
              }}>Roles</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '80px',
                whiteSpace: 'nowrap'
              }}>Status</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '110px',
                whiteSpace: 'nowrap'
              }}>Email Verified</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '120px',
                whiteSpace: 'nowrap'
              }}>Created</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '120px',
                whiteSpace: 'nowrap'
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {customersLoading ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : customers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography>No customers found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              customers.map((customer) => renderUserRow(customer))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={customerTotal}
        rowsPerPage={customerRowsPerPage}
        page={customerPage}
        onPageChange={(e, newPage) => setCustomerPage(newPage)}
        onRowsPerPageChange={(e) => {
          setCustomerRowsPerPage(parseInt(e.target.value, 10));
          setCustomerPage(0);
        }}
        sx={{
          borderTop: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-sunken)'
        }}
      />
    </>
  );

  const renderAdminsTab = () => (
    <>
      {/* Kitchen Management Style Action Bar */}
      <Box sx={{
        p: 3,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: { xs: 2, sm: 0 },
        backgroundColor: theme.components?.card?.background || '#ffffff'
      }}>
        <Box sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2
        }}>
          {/* Search Input - Kitchen Management Style */}
          <TextField
            placeholder="Search admins..."
            size="small"
            value={adminSearch}
            onChange={(e) => setAdminSearch(e.target.value)}
            sx={{
              minWidth: { xs: 250, sm: 300 },
              width: { xs: '100%', sm: 'auto' },
              '& .MuiOutlinedInput-root': {
                borderRadius: '10px',
                backgroundColor: theme.components?.card?.background || '#ffffff',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& fieldset': {
                  borderColor: theme.components?.input?.border || '#d1d5db',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover fieldset': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                },
                '&.Mui-focused fieldset': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                  borderWidth: '2px',
                  boxShadow: theme.components?.input?.focusRing || '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }
              }
            }}
            InputProps={{
              startAdornment: <MagnifyingGlassIcon className="h-4 w-4 text-gray-400 mr-2" />
            }}
          />

          {/* Enhanced Filter Dropdown */}
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <Select
              value={adminFilter}
              onChange={(e) => setAdminFilter(e.target.value)}
              displayEmpty
              sx={{
                borderRadius: '10px',
                backgroundColor: theme.components?.card?.background || '#ffffff',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.border || '#d1d5db',
                  transition: 'border-color 0.3s ease',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: theme.components?.input?.focus || '#3b82f6',
                  borderWidth: '2px',
                  boxShadow: theme.components?.input?.focusRing || '0 0 0 3px rgba(59, 130, 246, 0.1)',
                },
                '&:hover': {
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                }
              }}
            >
              <MenuItem value="" sx={{ '&:hover': { backgroundColor: 'var(--bg-sunken)' } }}>All Types</MenuItem>
              <MenuItem value="active" sx={{ '&:hover': { backgroundColor: 'var(--status-ok-bg)' } }}>Active Only</MenuItem>
              <MenuItem value="inactive" sx={{ '&:hover': { backgroundColor: 'var(--status-bad-bg)' } }}>Inactive Only</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Button
            onClick={fetchAdmins}
            variant="outlined"
            size="small"
            disabled={adminsLoading}
            sx={{
              minWidth: '40px',
              width: '40px',
              height: '40px',
              borderRadius: '8px',
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              color: 'var(--text-link) !important',
              borderWidth: '1px !important',
              borderStyle: 'solid !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.25) !important',
                borderColor: 'rgba(96, 165, 250, 0.4) !important',
              },
              '&:disabled': {
                opacity: 0.6
              }
            }}
          >
            <ArrowPathIcon className="h-4 w-4" />
          </Button>

          <Button
            startIcon={<ShieldCheckIcon className="h-4 w-4" />}
            variant="contained"
            size="small"
            onClick={() => { setFormError(null); setAdminForm({ username: '', email: '', password: '', confirmPassword: '', isActive: true }); setAddAdminOpen(true); }}
            sx={{
              borderRadius: '8px !important',
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1.5,
              backgroundColor: 'rgba(239, 68, 68, 0.2) !important',
              color: 'var(--status-bad) !important',
              borderWidth: '1px !important',
              borderStyle: 'solid !important',
              borderColor: 'rgba(239, 68, 68, 0.31) !important',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.25) !important',
                borderColor: 'rgba(239, 68, 68, 0.4) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Add Admin
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{
        boxShadow: 'none',
        width: '100%',
        flex: 1,
        overflowX: 'auto',
        '& .MuiTable-root': {
          minWidth: { xs: '900px', sm: '1000px' }
        }
      }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '140px',
                whiteSpace: 'nowrap'
              }}>Admin</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '200px',
                whiteSpace: 'nowrap'
              }}>Email</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '100px',
                whiteSpace: 'nowrap'
              }}>Roles</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '80px',
                whiteSpace: 'nowrap'
              }}>Status</TableCell>
              <TableCell sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '120px',
                whiteSpace: 'nowrap'
              }}>Created</TableCell>
              <TableCell align="center" sx={{
                fontWeight: 700,
                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                color: 'var(--text-secondary)',
                minWidth: '120px',
                whiteSpace: 'nowrap'
              }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {adminsLoading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : admins.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography>No admins found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              admins.map((admin) => renderUserRow(admin))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        component="div"
        count={adminTotal}
        rowsPerPage={adminRowsPerPage}
        page={adminPage}
        onPageChange={(e, newPage) => setAdminPage(newPage)}
        onRowsPerPageChange={(e) => {
          setAdminRowsPerPage(parseInt(e.target.value, 10));
          setAdminPage(0);
        }}
        sx={{
          borderTop: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-sunken)'
        }}
      />
    </>
  );

  const renderContent = () => (
    <Box sx={{ width: '100%', maxWidth: 'none' }}>
      {/* Kitchen Management Style - Single Clean Card */}
      <Paper sx={{
        width: '100%',
        maxWidth: 'none',
        borderRadius: '12px',
        border: '1px solid var(--border-subtle)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        overflow: 'hidden',
        backgroundColor: theme.components?.card?.background || '#ffffff',
        margin: 0,
        '& *': {
          maxWidth: 'none !important'
        }
      }}>
        {/* Enhanced Tab Navigation with Better Contrast */}
        <Box sx={{ p: 3, borderBottom: '1px solid var(--border-subtle)', backgroundColor: 'var(--bg-sunken)' }}>
          <Box sx={{
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            justifyContent: { xs: 'center', md: 'flex-start' }
          }}>
            <Button
              onClick={() => setTabValue(0)}
              variant={tabValue === 0 ? "contained" : "outlined"}
              startIcon={<BuildingStorefrontIcon className="h-4 w-4" />}
              sx={{
                borderRadius: '10px !important',
                px: 3.5,
                py: 1.5,
                textTransform: 'none !important',
                fontWeight: '700 !important',
                fontSize: '0.875rem !important',
                minWidth: '140px',
                backgroundColor: tabValue === 0 ? 'rgba(96, 165, 250, 0.2) !important' : '#ffffff !important',
                borderColor: tabValue === 0 ? 'rgba(96, 165, 250, 0.31) !important' : '#d1d5db !important',
                borderWidth: tabValue === 0 ? '2px !important' : '1px !important',
                color: tabValue === 0 ? '#2563eb !important' : '#374151 !important',
                boxShadow: 'none !important',
                '&:hover': {
                  backgroundColor: tabValue === 0 ? 'rgba(96, 165, 250, 0.25) !important' : '#f3f4f6 !important',
                  borderColor: tabValue === 0 ? 'rgba(96, 165, 250, 0.4) !important' : '#9ca3af !important',
                  boxShadow: 'none !important',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Vendors & Staff
            </Button>

            <Button
              onClick={() => setTabValue(1)}
              variant={tabValue === 1 ? "contained" : "outlined"}
              startIcon={<UserGroupIcon className="h-4 w-4" />}
              sx={{
                borderRadius: '10px !important',
                px: 3.5,
                py: 1.5,
                textTransform: 'none !important',
                fontWeight: '700 !important',
                fontSize: '0.875rem !important',
                minWidth: '120px',
                backgroundColor: tabValue === 1 ? 'rgba(16, 185, 129, 0.2) !important' : '#ffffff !important',
                borderColor: tabValue === 1 ? 'rgba(16, 185, 129, 0.31) !important' : '#d1d5db !important',
                borderWidth: tabValue === 1 ? '2px !important' : '1px !important',
                color: tabValue === 1 ? '#10B981 !important' : '#374151 !important',
                boxShadow: 'none !important',
                '&:hover': {
                  backgroundColor: tabValue === 1 ? 'rgba(16, 185, 129, 0.25) !important' : '#f3f4f6 !important',
                  borderColor: tabValue === 1 ? 'rgba(16, 185, 129, 0.4) !important' : '#9ca3af !important',
                  boxShadow: 'none !important',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Customers
            </Button>

            <Button
              onClick={() => setTabValue(2)}
              variant={tabValue === 2 ? "contained" : "outlined"}
              startIcon={<ShieldCheckIcon className="h-4 w-4" />}
              sx={{
                borderRadius: '10px !important',
                px: 3.5,
                py: 1.5,
                textTransform: 'none !important',
                fontWeight: '700 !important',
                fontSize: '0.875rem !important',
                minWidth: '100px',
                backgroundColor: tabValue === 2 ? 'rgba(239, 68, 68, 0.2) !important' : '#ffffff !important',
                borderColor: tabValue === 2 ? 'rgba(239, 68, 68, 0.31) !important' : '#d1d5db !important',
                borderWidth: tabValue === 2 ? '2px !important' : '1px !important',
                color: tabValue === 2 ? '#EF4444 !important' : '#374151 !important',
                boxShadow: 'none !important',
                '&:hover': {
                  backgroundColor: tabValue === 2 ? 'rgba(239, 68, 68, 0.25) !important' : '#f3f4f6 !important',
                  borderColor: tabValue === 2 ? 'rgba(239, 68, 68, 0.4) !important' : '#9ca3af !important',
                  boxShadow: 'none !important',
                },
                transition: 'all 0.2s ease',
              }}
            >
              Admins
            </Button>
          </Box>
        </Box>

        {/* Content Area - Kitchen Management Style */}
        <TabPanel value={tabValue} index={0}>
          {renderVendorsTab()}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {renderCustomersTab()}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {renderAdminsTab()}
        </TabPanel>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Delete User</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete user "{userToDelete?.username}"?
            This will deactivate their account and they won't be able to log in.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            variant="outlined"
            sx={{
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1,
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              color: 'var(--text-link) !important',
              borderRadius: '8px',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
                borderColor: 'rgba(96, 165, 250, 0.5) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteUser}
            variant="outlined"
            sx={{
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1,
              backgroundColor: 'rgba(239, 68, 68, 0.1) !important',
              borderColor: 'rgba(239, 68, 68, 0.3) !important',
              color: 'var(--status-bad) !important',
              borderRadius: '8px',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(239, 68, 68, 0.15) !important',
                borderColor: 'rgba(239, 68, 68, 0.4) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Customer Dialog */}
      <Dialog open={addCustomerOpen} onClose={() => setAddCustomerOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Customer</DialogTitle>
        <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {formError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                alignItems: 'center',
                '& .MuiAlert-action': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  alignItems: 'center',
                  marginTop: 0,
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1) !important',
                  border: '1px solid rgba(239, 68, 68, 0.2) !important',
                  color: `${theme.danger} !important`,
                  borderRadius: '8px',
                  padding: '4px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.2) !important',
                    borderColor: 'rgba(239, 68, 68, 0.3) !important',
                  }
                }
              }}
              onClose={() => setFormError(null)}
            >
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Username"
                fullWidth
                value={customerForm.username}
                onChange={(e) => setCustomerForm({ ...customerForm, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customerForm.isActive}
                    onChange={(e) => setCustomerForm({ ...customerForm, isActive: e.target.checked })}
                    sx={{
                      color: 'rgba(96, 165, 250, 0.5)',
                      '&.Mui-checked': {
                        color: 'var(--text-link) !important',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(96, 165, 250, 0.1)',
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                    Active
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={customerForm.email}
                onChange={(e) => setCustomerForm({ ...customerForm, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={customerForm.password}
                onChange={(e) => setCustomerForm({ ...customerForm, password: e.target.value })}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <AppIconButton
                        icon={showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        tooltip={showPassword ? "Hide password" : "Show password"}
                        variant="primary"
                        onClick={() => setShowPassword(v => !v)}
                        edge="end"
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={customerForm.confirmPassword}
                onChange={(e) => setCustomerForm({ ...customerForm, confirmPassword: e.target.value })}
                required
                error={customerForm.confirmPassword !== '' && customerForm.confirmPassword !== customerForm.password}
                helperText={customerForm.confirmPassword !== '' && customerForm.confirmPassword !== customerForm.password ? 'Passwords do not match' : ' '}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <AppIconButton
                        icon={showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        tooltip={showConfirmPassword ? "Hide password" : "Show password"}
                        variant="primary"
                        onClick={() => setShowConfirmPassword(v => !v)}
                        edge="end"
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddCustomerOpen(false)}
            disabled={creating}
            variant="outlined"
            sx={{
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1,
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              color: 'var(--text-link) !important',
              borderRadius: '8px',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
                borderColor: 'rgba(96, 165, 250, 0.5) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Cancel
          </Button>
          <Button onClick={async () => {
            // client validation
            if (!customerForm.username.trim()) return setFormError('Username is required');
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerForm.email)) return setFormError('Please enter a valid email');
            if (customerForm.password.length < 6) return setFormError('Password must be at least 6 characters');
            if (customerForm.password !== customerForm.confirmPassword) return setFormError('Passwords do not match');
            setFormError(null);
            setCreating(true);
            try {
              const payload = {
                username: customerForm.username.trim(),
                email: customerForm.email.trim(),
                password: customerForm.password,
                roles: ['ROLE_USER'],
                isActive: customerForm.isActive,
              };
              const res = await api.post('/admin/users', payload);
              toast.success(res.data?.message || 'Customer created');
              setAddCustomerOpen(false);
              fetchCustomers();
              fetchStats();
            } catch (err: any) {
              const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to create customer';
              setFormError(msg);
            } finally {
              setCreating(false);
            }
          }}
            variant="contained"
            disabled={creating}
            sx={{
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1,
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              color: 'var(--text-link) !important',
              border: '1px solid rgba(96, 165, 250, 0.31) !important',
              borderRadius: '8px',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
                borderColor: 'rgba(96, 165, 250, 0.5) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Admin Dialog */}
      <Dialog open={addAdminOpen} onClose={() => setAddAdminOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Admin</DialogTitle>
        <DialogContent sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
          {formError && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                alignItems: 'center',
                '& .MuiAlert-action': {
                  paddingTop: 0,
                  paddingBottom: 0,
                  alignItems: 'center',
                  marginTop: 0,
                },
                '& .MuiAlert-action .MuiIconButton-root': {
                  backgroundColor: 'rgba(239, 68, 68, 0.1) !important',
                  border: '1px solid rgba(239, 68, 68, 0.2) !important',
                  color: `${theme.danger} !important`,
                  borderRadius: '8px',
                  padding: '4px',
                  transition: 'all 0.2s',
                  '&:hover': {
                    backgroundColor: 'rgba(239, 68, 68, 0.2) !important',
                    borderColor: 'rgba(239, 68, 68, 0.3) !important',
                  }
                }
              }}
              onClose={() => setFormError(null)}
            >
              {formError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Username"
                fullWidth
                value={adminForm.username}
                onChange={(e) => setAdminForm({ ...adminForm, username: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={adminForm.isActive}
                    onChange={(e) => setAdminForm({ ...adminForm, isActive: e.target.checked })}
                    sx={{
                      color: 'rgba(96, 165, 250, 0.5)',
                      '&.Mui-checked': {
                        color: 'var(--text-link) !important',
                      },
                      '&:hover': {
                        backgroundColor: 'rgba(96, 165, 250, 0.1)',
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontWeight: 600, color: theme.text }}>
                    Active
                  </Typography>
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Email"
                type="email"
                fullWidth
                value={adminForm.email}
                onChange={(e) => setAdminForm({ ...adminForm, email: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Password"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={adminForm.password}
                onChange={(e) => setAdminForm({ ...adminForm, password: e.target.value })}
                required
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <AppIconButton
                        icon={showPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        tooltip={showPassword ? "Hide password" : "Show password"}
                        variant="primary"
                        onClick={() => setShowPassword(v => !v)}
                        edge="end"
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                fullWidth
                value={adminForm.confirmPassword}
                onChange={(e) => setAdminForm({ ...adminForm, confirmPassword: e.target.value })}
                required
                error={adminForm.confirmPassword !== '' && adminForm.confirmPassword !== adminForm.password}
                helperText={adminForm.confirmPassword !== '' && adminForm.confirmPassword !== adminForm.password ? 'Passwords do not match' : ' '}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <AppIconButton
                        icon={showConfirmPassword ? <EyeSlashIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
                        tooltip={showConfirmPassword ? "Hide password" : "Show password"}
                        variant="primary"
                        onClick={() => setShowConfirmPassword(v => !v)}
                        edge="end"
                      />
                    </InputAdornment>
                  )
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAddAdminOpen(false)}
            disabled={creating}
            variant="outlined"
            sx={{
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1,
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              borderColor: 'rgba(96, 165, 250, 0.31) !important',
              color: 'var(--text-link) !important',
              borderRadius: '8px',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
                borderColor: 'rgba(96, 165, 250, 0.5) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Cancel
          </Button>
          <Button onClick={async () => {
            if (!adminForm.username.trim()) return setFormError('Username is required');
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(adminForm.email)) return setFormError('Please enter a valid email');
            if (adminForm.password.length < 6) return setFormError('Password must be at least 6 characters');
            if (adminForm.password !== adminForm.confirmPassword) return setFormError('Passwords do not match');
            setFormError(null);
            setCreating(true);
            try {
              const payload = {
                username: adminForm.username.trim(),
                email: adminForm.email.trim(),
                password: adminForm.password,
                roles: ['ROLE_ADMIN'],
                isActive: adminForm.isActive,
              };
              const res = await api.post('/admin/users', payload);
              toast.success(res.data?.message || 'Admin created');
              setAddAdminOpen(false);
              fetchAdmins();
              fetchStats();
            } catch (err: any) {
              const msg = err?.response?.data?.message || err?.response?.data?.error || err?.message || 'Failed to create admin';
              setFormError(msg);
            } finally {
              setCreating(false);
            }
          }}
            variant="contained"
            disabled={creating}
            sx={{
              textTransform: 'none !important',
              fontWeight: '600 !important',
              px: 3,
              py: 1,
              backgroundColor: 'rgba(96, 165, 250, 0.2) !important',
              color: 'var(--text-link) !important',
              border: '1px solid rgba(96, 165, 250, 0.31) !important',
              borderRadius: '8px',
              boxShadow: 'none !important',
              '&:hover': {
                backgroundColor: 'rgba(96, 165, 250, 0.3) !important',
                borderColor: 'rgba(96, 165, 250, 0.5) !important',
                boxShadow: 'none !important',
              }
            }}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  return (
    <Box sx={{
      p: 3,
      backgroundColor: theme.background,
      minHeight: '100vh',
      width: '100%',
      maxWidth: 'none',
      margin: 0,
      paddingLeft: { xs: 2, sm: 3 },
      paddingRight: { xs: 2, sm: 3 }
    }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: theme.text, mb: 1 }}>
          User Management
        </Typography>
        <Typography variant="body1" sx={{ color: theme.gray?.[600] }}>
          Manage system users, vendors, and permissions
        </Typography>
      </Box>

      {renderContent()}
    </Box>
  );
};

export default UserManagement;